import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Shield, User as UserIcon, Save } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast, Toaster } from 'sonner';
import { listUsers, createUser, updateUser, deleteUser, listBranches } from '../lib/api';

const emptyForm = () => ({
  username: '',
  full_name: '',
  email: '',
  password: '',
  role: 'kasir',
  branch_ids: [],
});

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, b] = await Promise.all([listUsers(), listBranches()]);
      setUsers(u);
      setBranches(b);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (u) => {
    setForm({
      username: u.username,
      full_name: u.full_name || '',
      email: u.email || '',
      password: '',
      role: u.role,
      branch_ids: u.branch_ids || [],
    });
    setEditingId(u.id);
    setOpen(true);
  };

  const toggleBranch = (id) => {
    setForm((f) => ({
      ...f,
      branch_ids: f.branch_ids.includes(id) ? [] : [id],
    }));
  };

  const save = async () => {
    try {
      if (editingId) {
        const payload = {
          full_name: form.full_name,
          email: form.email,
          role: form.role,
          branch_ids: form.role === 'admin' ? [] : form.branch_ids,
        };
        if (form.password) payload.password = form.password;
        await updateUser(editingId, payload);
        toast.success('User diperbarui');
      } else {
        if (!form.username || !form.password) {
          toast.error('Username dan password wajib');
          return;
        }
        await createUser({
          username: form.username,
          password: form.password,
          full_name: form.full_name,
          email: form.email,
          role: form.role,
          branch_ids: form.role === 'admin' ? [] : form.branch_ids,
        });
        toast.success('User ditambahkan');
      }
      setOpen(false);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Gagal menyimpan');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus user ${u.username}?`)) return;
    try {
      await deleteUser(u.id);
      toast.success('User dihapus');
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Gagal menghapus');
    }
  };

  const branchNamesFor = (ids) => {
    if (!ids || !ids.length) return '—';
    return branches
      .filter((b) => ids.includes(b.id))
      .map((b) => b.name)
      .join(', ') || '—';
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-right" richColors />
      <AppHeader />
      <main className="max-w-[1400px] mx-auto px-6 md:px-8 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
              Manajemen Akun
            </div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight">
              Kelola Kasir & Admin
            </h1>
            <p className="text-sm text-stone-600 mt-2">
              Setiap kasir hanya bisa mengakses cabang yang ditugaskan. Admin dapat melihat semua cabang.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah User
          </Button>
        </div>

        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full ledger" data-testid="users-table">
            <thead>
              <tr>
                <th style={{ width: '4%' }}></th>
                <th style={{ width: '18%' }}>Username</th>
                <th style={{ width: '20%' }}>Nama Lengkap</th>
                <th>Email</th>
                <th style={{ width: '10%' }}>Role</th>
                <th>Cabang</th>
                <th style={{ width: '10%' }}>Status</th>
                <th style={{ width: '110px' }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.username}`}>
                  <td>
                    {u.role === 'admin' ? (
                      <Shield className="w-4 h-4 text-emerald-800" />
                    ) : (
                      <UserIcon className="w-4 h-4 text-stone-500" />
                    )}
                  </td>
                  <td className="font-semibold text-stone-900">{u.username}</td>
                  <td className="text-stone-700">{u.full_name || '—'}</td>
                  <td className="text-stone-600 text-sm">{u.email || '—'}</td>
                  <td>
                    <span
                      className={`inline-block text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded ${
                        u.role === 'admin'
                          ? 'bg-emerald-100 text-emerald-900 border border-emerald-200'
                          : 'bg-stone-100 text-stone-700 border border-stone-200'
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="text-sm text-stone-700">
                    {u.role === 'admin' ? (
                      <span className="text-stone-500 italic">semua cabang</span>
                    ) : (
                      branchNamesFor(u.branch_ids)
                    )}
                  </td>
                  <td>
                    <span
                      className={`inline-block text-[11px] font-medium px-2 py-1 rounded ${
                        u.is_active
                          ? 'text-emerald-800 bg-emerald-50 border border-emerald-200'
                          : 'text-red-800 bg-red-50 border border-red-200'
                      }`}
                    >
                      {u.is_active ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(u)}
                        data-testid={`edit-user-${u.username}`}
                      >
                        Ubah
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(u)}
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        data-testid={`delete-user-${u.username}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="text-center text-stone-500 py-8">
                    Belum ada user.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? 'Ubah User' : 'Tambah User Baru'}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Ubah data. Password kosongkan jika tidak ingin mengganti.'
                : 'Isi data user. Username tidak bisa diubah setelah dibuat.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Username *
              </Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={!!editingId}
                data-testid="user-form-username"
                placeholder="kasir1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Password {editingId && <span className="text-stone-400">(opsional)</span>}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-form-password"
                placeholder="••••••"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Nama Lengkap
              </Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="user-form-fullname"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Email
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-form-email"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Role
              </Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger data-testid="user-form-role" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kasir">Kasir</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.role === 'kasir' && (
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-2 block">
                Cabang yang Dapat Diakses (1 kasir = 1 cabang)
              </Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-stone-200 rounded-md p-3 bg-stone-50">
                {branches.map((b) => (
                  <label
                    key={b.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                    data-testid={`user-form-branch-${b.name}`}
                  >
                    <Checkbox
                      checked={form.branch_ids.includes(b.id)}
                      onCheckedChange={() => toggleBranch(b.id)}
                    />
                    <span>{b.name}</span>
                  </label>
                ))}
                {branches.length === 0 && (
                  <span className="text-stone-500 text-xs col-span-2">
                    Belum ada cabang.
                  </span>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="user-form-cancel">
              Batal
            </Button>
            <Button
              onClick={save}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="user-form-save"
            >
              <Save className="w-4 h-4 mr-1" /> Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
